<template>
  <div
    class="select-mode"
    :class="{ 'has-error': bookingModeError }"
  >
    <n-select
      v-model:value="localValue"
      size="large"
      :options="options"
      :placement="isDesktop ? 'top' : 'bottom'"
      :render-label="renderLabel"
      :render-tag="renderTag"
      label-field="label"
      value-field="value"
      children-field="children"
      :show-checkmark="false"
      :theme-overrides="selectThemeOverrides"
      :to="body"
      :default-value="options[0].children[0].value"
      @update:value="updateHandler"
    />
    <div class="select-mode-feedback">
      {{ translationsStore.t("searchbar_error_mode") }}
    </div>
  </div>
</template>

<script setup>
import { watch, ref, h } from "vue";
import { useRoute } from "vue-router";
import { NSelect } from "naive-ui";
import { useViewport } from "../composables/useViewport";
import { useTranslationsStore } from "../stores/translations";
import { useBookingStore } from "../stores/booking";

const translationsStore = useTranslationsStore();
const bookingStore = useBookingStore();
const isDesktop = useViewport().isDesktop;
const props = defineProps({
  options: {
    type: Object,
    default: () => {},
  },
  modelValue: {
    type: String,
    default: () => "",
  },
  isBookingHome: Boolean,
  bookingModeError: Boolean,
});
const emit = defineEmits(["modeChanged", "update:modelValue"]);
// keep localValue in sync if parent changes (e.g. on initial load)
const route = useRoute();
watch(
  () => props.modelValue,
  (newVal) => {
    localValue.value = newVal;
  }
);
const updateHandler = (mode) => {
  emit("update:modelValue", mode);
  emit("modeChanged", mode);
};
const selectThemeOverrides = {
  peers: {
    InternalSelection: {
      heightLarge: props.isBookingHome ? "4rem" : "3.5rem",
      color: "transparent",
      colorActive: "transparent",
      textColor: "rgb(var(--brand-black) / 1)",
      paddingSingle: "0 2.5rem 0 1.25rem",
      border: "none",
      borderActive: "none",
      borderFocus: "none",
      borderHover: "none",
    },
    InternalSelectMenu: {
      optionColorPending: "rgb(var(--brand-primary) / 1)", // optional override
    },
  },
};
const localValue = ref(props.modelValue);
const renderLabel = (option) => {
  return option.label;
};
const renderTag = ({ option }) => {
  return h(
    "span",
    `${translationsStore.t("search_bar_select_group_label")} ${option.label}`
  );
};

watch(
  () => bookingStore.journeyType,
  () => {
    if (route.query.variant) {
      localValue.value = bookingStore.journeyType;
      emit("update:modelValue", bookingStore.journeyType);
      emit("modeChanged", bookingStore.journeyType);
    }
  }
);
</script>

<style scoped>
.select-mode {
  height: calc(100% - 2px);
  border: 1px solid rgba(0 0 0 / 10%);
  text-transform: uppercase;
  :deep(.n-base-selection) {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.44px;
  }
  @media screen and (min-width: 1000px) {
    border: none;
    max-width: 14.6875rem;
    min-width: 14.6875rem;
  }

  :deep(.n-base-suffix) {
    right: 20px;
  }
  :deep(.n-base-selection-label) {
    @media screen and (min-width: 1000px) {
      border-right: 1px solid rgba(0 0 0 / 10%);
    }
  }
}
.select-mode.glass {
  border: none;
  :deep(.n-base-selection) {
    @media screen and (min-width: 1000px) {
      --n-color: transparent;
      --n-color-active: transparent;
      --n-text-color: rgb(var(--brand-white) / 1);
    }
  }
  :deep(.n-base-selection-label) {
    border-right: 1px solid rgba(255 255 255 / 10%);
  }
}
@media screen and (max-width: 999px) {
  :deep(.n-select) {
    box-sizing: border-box;
    border: 1px solid #e6e6e6;
  }
}

.select-mode-feedback {
  color: rgb(var(--brand-black) / 1);
  position: absolute;
  display: none;
  bottom: -30px;
  text-transform: none;
  font-size: 0.8125rem;
}

.select-mode.has-error {
  position: relative;
  margin-bottom: 40px;
  border: 1px solid rgb(var(--brand-black) / 1);
}

.select-mode.has-error .select-mode-feedback {
  display: block;
}

@media screen and (min-width: 1000px) {
  .select-mode.has-error {
    margin-bottom: 0px;
  }

  .select-mode.glass.has-error {
    border-color: rgb(var(--brand-white) / 1);
  }

  .select-mode.has-error .select-mode-feedback {
    bottom: -22px;
  }

  .select-mode.has-error.glass .select-mode-feedback {
    bottom: -48px;
  }

  .glass .select-mode-feedback {
    color: rgb(var(--brand-white) / 1);
  }
}
</style>

<style>
.n-virtual-list {
  --n-option-color-selected: rgb(var(--brand-primary) / 1);
  --n-option-color-active-pending: rgb(var(--brand-primary) / 1);
  --n-option-text-color-active: rgb(var(--brand-white) / 1);
  --n-option-font-size: 0.75rem;
  margin: 1rem 1.25rem;
}
.n-base-select-menu {
  line-height: 2.5rem;
}
.n-base-select-menu .n-base-select-option {
  text-transform: uppercase;
}
.n-base-select-menu
  .n-base-select-option.n-base-select-option--selected:before {
  background-color: rgb(var(--brand-primary) / 1);
}
.n-base-select-menu
  .n-base-select-option:not(.n-base-select-option--selected):hover {
  text-decoration: underline;
}
.n-base-select-menu
  .n-base-select-option--pending:not(.n-base-select-option--selected):before {
  background-color: transparent;
}
.n-base-select-menu
  .n-base-select-option--pending:not(.n-base-select-option--selected) {
  text-decoration: underline;
}
.n-base-selection.n-base-selection--active .n-base-icon {
  transform: rotate(180deg);
}
</style>
